import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Backdrop, Box, IconButton } from '@mui/material';
import { ArrowCheckIcon, MenuIcon } from '@sharedCR/coreDesignSystem/icons';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getIsMobileLayoutForced, getIsNativePlatform } from 'utils/platform';

import { setIsSidebarOpenAction } from 'store/global/actions';
import {
  getAssignedToApplicationUsers,
  resetAssignedToApplicationUsers,
} from 'store/loanApplications/actions';
import {
  selectAssignedUsers,
  selectSecondaryLoanHeaderView,
} from 'store/loanApplications/selectors';

import LoanInfoHeader from '../RedesignedLoanInfoHeader';

import HeaderViewList from './HeaderViewList';
import {
  backdropStyles,
  expandToggleStyles,
  getContentStyles,
  wrapperStyles,
} from './MainHeader.styles';
import { MainHeaderContentTestIds } from './MainHeaderContent.testIds';

const BACKDROP_CLASSNAME = 'backdrop';

const MainHeaderContent = ({ onOpenSearch, isModifierHeld, shortcutPrefix }) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const [isExpanded, setIsExpanded] = useState(false);

  const assignedUsers = useSelector(selectAssignedUsers);
  const secondaryLoanHeaderView = useSelector(selectSecondaryLoanHeaderView);

  const { isDesktop, isMobile } = useBreakpoints();

  // On the native/forced mobile-nav layout the desktop MenuSidebar is not rendered, so its hamburger
  // would open a sidebar that doesn't exist — hide it there. Tablet/narrow web keeps it.
  const isMobileNav = getIsNativePlatform() || getIsMobileLayoutForced();

  const isExpandToggleAvailable = !isMobile && !!secondaryLoanHeaderView?.length;

  const onOpenSidebar = useCallback(() => {
    dispatch(setIsSidebarOpenAction(true));
  }, [dispatch]);

  const ref = useRef(null);
  const headerRef = useRef(null);
  const pageSidebarRef = useRef(null);

  const openStateClassname = clsx({ open: isExpanded });

  useEffect(() => {
    headerRef.current = document.querySelector('#header');
    pageSidebarRef.current = document.querySelector('.page-sidebar');
  }, []);

  useEffect(() => {
    if (!ref.current || !isExpanded) {
      return;
    }

    const currentRef = ref.current;

    const handleWheel = e => {
      e.preventDefault();
    };

    currentRef.addEventListener('wheel', handleWheel);

    return () => currentRef.removeEventListener('wheel', handleWheel);
  }, [isExpanded]);

  useEffectSafeStrictMode(() => {
    if (!applicationId) {
      return;
    }

    dispatch(getAssignedToApplicationUsers({ applicationId })).catch(Error);

    return () => {
      dispatch(resetAssignedToApplicationUsers());
    };
  }, [dispatch, applicationId]);

  const onToggleSummary = useCallback(() => {
    setIsExpanded(prev => {
      const currentHeader = headerRef.current;
      const currentPageSidebar = pageSidebarRef.current;

      const method = !prev ? 'add' : 'remove';
      currentHeader?.classList[method](BACKDROP_CLASSNAME);
      currentPageSidebar?.classList[method](BACKDROP_CLASSNAME);

      return !prev;
    });
  }, []);

  return (
    <>
      <Box ref={ref} sx={wrapperStyles}>
        <Box className={openStateClassname} sx={getContentStyles(isMobile)}>
          {!isDesktop && !isMobileNav && (
            <IconButton
              data-testid={MainHeaderContentTestIds.OPEN_SIDEBAR_BUTTON}
              onClick={onOpenSidebar}
            >
              <MenuIcon size={IconSize[20]} />
            </IconButton>
          )}

          <HeaderViewList
            assignedUsers={assignedUsers}
            secondaryLoanHeaderView={secondaryLoanHeaderView}
          />

          {isExpandToggleAvailable && (
            <IconButton
              disableRipple
              data-testid={MainHeaderContentTestIds.EXPAND_TOGGLE_BUTTON}
              sx={expandToggleStyles}
              onClick={onToggleSummary}
            >
              <ArrowCheckIcon direction={isExpanded ? 'up' : 'down'} size={IconSize[16]} />
            </IconButton>
          )}
        </Box>

        <LoanInfoHeader
          isExpanded={isExpanded}
          isModifierHeld={isModifierHeld}
          shortcutPrefix={shortcutPrefix}
          onOpenSearch={onOpenSearch}
          onToggleSummary={onToggleSummary}
        />
      </Box>

      <Backdrop open={isExpanded} sx={backdropStyles} onClick={onToggleSummary} />
    </>
  );
};

MainHeaderContent.propTypes = {
  onOpenSearch: PropTypes.func.isRequired,
  isModifierHeld: PropTypes.bool,
  shortcutPrefix: PropTypes.string,
};

export default MainHeaderContent;
